import { useAuth } from "@/hooks/use-auth";
import { PublicLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardFooter } from "@/components/ui/card";
import { useForm, useWatch } from "react-hook-form";
import { useState, useEffect, useMemo } from "react";
import { Link } from "wouter";
import { Eye, EyeOff, UserPlus, CheckCircle2, Gift, Search, Building2, ArrowLeft, ArrowRight, Check, X } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { useListPublicGyms } from "@workspace/api-client-react";
import { PasswordStrengthMeter } from "@/components/password-strength-meter";

function PolicyRow({ met, label }: { met: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {met ? (
        <Check className="w-3.5 h-3.5 text-green-400 shrink-0" />
      ) : (
        <X className="w-3.5 h-3.5 text-white/30 shrink-0" />
      )}
      <span className={`text-xs ${met ? "text-green-400" : "text-white/40"}`}>{label}</span>
    </div>
  );
}

interface ReferralInfo {
  valid: boolean;
  referrerGymId: string;
  referrerGymName: string;
}

export default function Register() {
  const { register: registerUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedGymId, setSelectedGymId] = useState<string | null>(null);
  const [gymSearch, setGymSearch] = useState("");

  const { data: gyms, isLoading: gymsLoading } = useListPublicGyms();

  const { register, handleSubmit, formState: { errors }, trigger, control } = useForm({
    defaultValues: { email: "", password: "", firstName: "", lastName: "", phone: "" }
  });

  const passwordValue = useWatch({ control, name: "password", defaultValue: "" });
  const meetsLength    = passwordValue.length >= 8;
  const hasUppercase   = /[A-Z]/.test(passwordValue);
  const hasNumber      = /[0-9]/.test(passwordValue);
  const hasSpecial     = /[^A-Za-z0-9]/.test(passwordValue);
  const policyMet      = meetsLength && hasUppercase && hasNumber && hasSpecial;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get("ref");
    if (refCode) {
      setReferralLoading(true);
      apiFetch(`/referral/validate/${encodeURIComponent(refCode)}`)
        .then((data: ReferralInfo) => {
          if (data.valid) {
            setReferralInfo(data);
          }
        })
        .catch(() => {})
        .finally(() => setReferralLoading(false));
    }
  }, []);

  useEffect(() => {
    if (!gyms || gyms.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const gymSlug = params.get("gym");
    if (gymSlug && !selectedGymId) {
      const matched = gyms.find((g) => g.slug === gymSlug);
      if (matched) {
        setSelectedGymId(matched.id);
      }
    }
  }, [gyms]);

  const filteredGyms = useMemo(() => {
    if (!gyms) return [];
    if (!gymSearch.trim()) return gyms;
    const q = gymSearch.toLowerCase();
    return gyms.filter(
      (g) =>
        g.name.toLowerCase().includes(q) ||
        (g.address && g.address.toLowerCase().includes(q))
    );
  }, [gyms, gymSearch]);

  const handleStep1Next = async () => {
    const valid = await trigger(["firstName", "lastName", "email", "password"]);
    if (valid) setStep(2);
  };

  const onSubmit = async (data: { email: string; password: string; firstName: string; lastName: string; phone: string }) => {
    setIsSubmitting(true);
    try {
      const payload: Record<string, string> = { ...data, role: "STUDENT" };
      if (referralInfo?.referrerGymId) {
        const ref = new URLSearchParams(window.location.search).get("ref");
        if (ref) payload.referralCode = ref;
      }
      if (selectedGymId) {
        payload.gymId = selectedGymId;
      }
      await registerUser(payload);
    } catch (_) {
      // Error already surfaced to user via toast in useAuth hook
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <SEOHead
        title="Create an Account — Start Booking Private Lessons"
        description="Sign up for Open Mat to book private martial arts lessons, track your sessions, and connect with trainers at your gym."
        canonicalPath="/register"
        noindex
      />
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md glass-panel border-white/5 bg-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
              <UserPlus className="w-7 h-7 text-primary" />
            </div>
            <h1 className="text-3xl font-display font-bold text-white uppercase tracking-wide leading-none">Create Account</h1>
            <CardDescription className="text-muted-foreground mt-2">
              {step === 1 ? "Join OPEN MAT and start training" : "Select your home gym"}
            </CardDescription>
            <div className="flex items-center justify-center gap-2 mt-4">
              <div className={`w-8 h-1 rounded-full transition-colors ${step >= 1 ? "bg-primary" : "bg-white/10"}`} />
              <div className={`w-8 h-1 rounded-full transition-colors ${step >= 2 ? "bg-primary" : "bg-white/10"}`} />
            </div>
          </CardHeader>
          <CardContent className="pt-6">
            {referralLoading && (
              <div className="mb-4 p-3 rounded-xl border border-white/10 bg-white/5 text-sm text-muted-foreground text-center">
                Validating referral code...
              </div>
            )}
            {referralInfo && (
              <div className="mb-4 p-3 rounded-xl border border-primary/30 bg-primary/5 flex items-center gap-3">
                <Gift className="w-5 h-5 text-primary shrink-0" />
                <div className="text-sm">
                  <span className="text-muted-foreground">Referred by </span>
                  <span className="text-white font-semibold">{referralInfo.referrerGymName}</span>
                </div>
                <CheckCircle2 className="w-4 h-4 text-green-400 ml-auto shrink-0" />
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} onKeyDown={(e) => { if (e.key === "Enter" && step === 1) { e.preventDefault(); handleStep1Next(); } }}>
              {step === 1 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <Input id="firstName" placeholder="John" className="bg-background border-white/10 focus:border-primary h-12 rounded-xl" {...register("firstName", { required: true })} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input id="lastName" placeholder="Doe" className="bg-background border-white/10 focus:border-primary h-12 rounded-xl" {...register("lastName", { required: true })} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" placeholder="you@example.com" className="bg-background border-white/10 focus:border-primary h-12 rounded-xl" {...register("email", { required: true })} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input id="phone" placeholder="+1 (555) 000-0000" className="bg-background border-white/10 focus:border-primary h-12 rounded-xl" {...register("phone")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Min 8 characters"
                        className="bg-background border-white/10 focus:border-primary h-12 rounded-xl pr-12"
                        {...register("password", {
                          required: true,
                          validate: () => policyMet || "Password does not meet requirements",
                        })}
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {passwordValue.length > 0 && (
                      <div className="p-3 rounded-lg bg-white/5 border border-white/8 space-y-2">
                        <PasswordStrengthMeter password={passwordValue} />
                        <div className="space-y-1.5">
                          <PolicyRow met={meetsLength}  label="At least 8 characters" />
                          <PolicyRow met={hasUppercase} label="At least one uppercase letter" />
                          <PolicyRow met={hasNumber}    label="At least one number" />
                          <PolicyRow met={hasSpecial}   label="At least one special character (!@#$…)" />
                        </div>
                      </div>
                    )}
                    {errors.password && (
                      <p className="text-xs text-red-400">{errors.password.message as string}</p>
                    )}
                  </div>
                  <Button type="button" onClick={handleStep1Next} className="w-full h-14 text-lg font-bold bg-gradient-to-r from-primary to-[#f0d060] text-black rounded-xl mt-2">
                    Next <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search gyms by name or location..."
                      value={gymSearch}
                      onChange={(e) => setGymSearch(e.target.value)}
                      className="pl-11 bg-background border-white/10 focus:border-primary h-12 rounded-xl"
                    />
                  </div>

                  <div className="max-h-64 overflow-y-auto space-y-2 scrollbar-thin">
                    {gymsLoading ? (
                      <div className="space-y-2">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="h-16 bg-white/5 rounded-xl animate-pulse" />
                        ))}
                      </div>
                    ) : filteredGyms.length === 0 ? (
                      <div className="text-center py-8">
                        <Building2 className="w-8 h-8 text-white/20 mx-auto mb-2" />
                        {gyms && gyms.length === 0 ? (
                          <>
                            <p className="text-sm font-medium text-white mb-1">No gyms available yet</p>
                            <p className="text-xs text-muted-foreground">Check back soon — new gyms are being added regularly.</p>
                          </>
                        ) : (
                          <p className="text-sm text-muted-foreground">No gyms match your search</p>
                        )}
                      </div>
                    ) : (
                      filteredGyms.map((gym) => (
                        <button
                          key={gym.id}
                          type="button"
                          onClick={() => setSelectedGymId(selectedGymId === gym.id ? null : gym.id)}
                          className={`w-full p-3 rounded-xl border text-left transition-all flex items-center gap-3 ${
                            selectedGymId === gym.id
                              ? "border-primary bg-primary/10"
                              : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                          }`}
                        >
                          <div className="w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                            {gym.logoUrl ? (
                              <img src={gym.logoUrl} alt="" className="w-full h-full object-cover rounded-lg" />
                            ) : (
                              <Building2 className="w-4 h-4 text-primary" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white truncate">{gym.name}</p>
                            {gym.address && (
                              <p className="text-xs text-muted-foreground truncate">{gym.address}</p>
                            )}
                          </div>
                          {selectedGymId === gym.id && (
                            <Check className="w-5 h-5 text-primary shrink-0" />
                          )}
                        </button>
                      ))
                    )}
                  </div>

                  <div className="flex gap-3 mt-4">
                    <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 h-14 text-lg border-white/10 hover:bg-white/5 rounded-xl">
                      <ArrowLeft className="w-5 h-5 mr-2" /> Back
                    </Button>
                    <Button type="submit" disabled={isSubmitting || !selectedGymId} className="flex-1 h-14 text-lg font-bold bg-gradient-to-r from-primary to-[#f0d060] text-black rounded-xl disabled:opacity-50">
                      {isSubmitting ? "Creating..." : "Create Account"}
                    </Button>
                  </div>

                  {!selectedGymId && (
                    <p className="text-xs text-center text-muted-foreground">
                      Please select your home gym to continue.
                    </p>
                  )}
                </div>
              )}
            </form>
          </CardContent>
          <CardFooter className="justify-center text-sm text-muted-foreground border-t border-white/5 pt-6">
            Already have an account? <Link href="/login"><span className="text-primary hover:underline ml-1 cursor-pointer">Sign in</span></Link>
          </CardFooter>
        </Card>
      </div>
    </PublicLayout>
  );
}
